import { useState } from 'react';
import { TIMESTAMP_PARAM } from '../urls';

const getZipInstance = async () => {
  const JSZip = (await import('jszip')).default;
  return new JSZip();
};

const getAxiosInstance = async () => {
  const axios = (await import('axios')).default;
  return axios;
};

const MIME_EXTENSION_MAP: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'application/json': 'json',
  'application/octet-stream': 'bin',
  'text/plain': 'txt',
  'text/html': 'html',
};

const DEFAULT_FILENAME_PREFIX = 'mycollection';

/**
 * Extract file name from headers (Content-Disposition preferred),
 * fallback to URL path, and enforce extension from Content-Type.
 */
const getFileName = (url: string, headers?: Record<string, string>, fallbackIndex?: number) => {
  // 1. Content-Disposition wins
  const cd = headers?.['content-disposition'];
  if (cd) {
    const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
  }

  // 2. Fallback to URL basename
  const urlObj = new URL(url);
  let baseName = urlObj.pathname.split('/').pop() || `file_${fallbackIndex ?? ''}`;

  // Strip extension — will enforce based on content-type
  const dotIndex = baseName.lastIndexOf('.');
  if (dotIndex > -1) {
    baseName = baseName.substring(0, dotIndex);
  }

  // 3. Get extension from Content-Type (takes precedence)
  const contentType = headers?.['content-type'];
  let ext = '';
  if (contentType) {
    const normalized = contentType.split(';')[0].trim(); // remove charset
    ext = MIME_EXTENSION_MAP[normalized] || normalized.split('/')[1] || '';
  }

  // 4. If no content-type, fallback to URL’s extension
  if (!ext && dotIndex > -1) {
    ext = urlObj.pathname.split('.').pop() ?? '';
  }

  // 5. Final fallback
  return `${baseName}.${ext || 'bin'}`;
};

type FileDetail = Readonly<{
  fileUrl: string;
  fileName: string;
}>;

type UseFileDownloadProps = Readonly<{
  onZipDownloadError?: (fileUrls: string[]) => void;
  onMultipleFilesDownloadError?: (fileDetails: FileDetail[]) => void;
  onSingleFileDownloadError?: (fileUrl: string, fileName: string) => void;
}>;

export const useFileDownload = ({ onZipDownloadError, onMultipleFilesDownloadError, onSingleFileDownloadError }: UseFileDownloadProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  /** Helper: trigger download for a blob */
  const triggerDownload = (blob: Blob, fileName: string) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  /** Download a single file */
  const downloadSingleFile = async (fileUrl: string, fileName?: string) => {
    setLoading(true);
    setError(null);
    try {
      const axios = await getAxiosInstance();
      const response = await axios.get(fileUrl, { responseType: 'blob' });

      const name = fileName || getFileName(fileUrl, response.headers as Record<string, string>, 1);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      triggerDownload(blob, name);
    } catch (err) {
      setError(err);
      onSingleFileDownloadError?.(fileUrl, getFileName(fileUrl));
    } finally {
      setLoading(false);
    }
  };

  /** Download multiple files as a ZIP */
  const downloadMultipleFiles = async (fileUrls: string[], fileNamePrefix: string = DEFAULT_FILENAME_PREFIX) => {
    setLoading(true);
    setError(null);

    try {
      const zip = await getZipInstance();
      const axios = await getAxiosInstance();

      const results = await Promise.allSettled(
        fileUrls.map(async (url, i) => {
          const response = await axios.get(url, { responseType: 'blob' });
          const name = getFileName(url, response.headers as Record<string, string>, i + 1);
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          zip.file(name, blob);
          return name;
        }),
      );

      // Collect failures
      const failedFiles: FileDetail[] = [];
      results.forEach((res, i) => {
        if (res.status === 'rejected') {
          const failedName = getFileName(fileUrls[i], {}, i + 1);
          failedFiles.push({ fileUrl: fileUrls[i], fileName: failedName });
        }
      });

      // Generate and trigger ZIP download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = `${fileNamePrefix}-${TIMESTAMP_PARAM}.zip`;
      triggerDownload(zipBlob, fileName);

      // Notify user if some files failed
      if (failedFiles.length) {
        onMultipleFilesDownloadError?.(failedFiles);
      }
    } catch (err) {
      setError(err);
      onZipDownloadError?.(fileUrls);
    } finally {
      setLoading(false);
    }
  };

  return { downloadSingleFile, downloadMultipleFiles, loading, error };
};
